import React, { useEffect, useRef } from 'react';
import { useDispatch } from "react-redux";

import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import WarningIcon from "@mui/icons-material/Warning";
import StarsIcon from "@mui/icons-material/Stars";
import NoteIcon from "@mui/icons-material/Note";

import useDataSource from "../../hooks/useDataSource";

import {
	appSetTooltipContent
} from "../../redux/app/appActions";
import {
	getFieldFormatters
} from "../../utilities/DataSourceUtilities";
import {
	formatValue
} from "../../utilities/FormatUtilities";

import { styled, Typography } from '@mui/material';

const PREFIX = 'TooltipIcon';

const classes = {
    root: `${PREFIX}-root`,
    modalButton: `${PREFIX}-modalButton`,
    tooltipPopover: `${PREFIX}-tooltipPopover`,
    tooltipNotePopover: `${PREFIX}-tooltipNotePopover`
};

const StyledTypography = styled(Typography)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
		height: '100%',
		width: '100%'
	},

    [`& .${classes.modalButton}`]: {
		position: 'absolute',
		color: theme.palette.grey[500],
		zIndex: 99
	},

    [`& .${classes.tooltipPopover}`]: {
		position: 'absolute',
		color: theme.palette.grey[500],
		zIndex: 99
	},

    [`& .${classes.tooltipNotePopover}`]: {
		position: 'absolute',
		//color: theme.palette.grey[500],
		zIndex: 99
	}
}));

export default function TooltipIcons({tooltip, focused}) {
	const dispatch = useDispatch();

	const tooltipElement = useRef(null);

	// console.log('******TOOLTIP******', tooltip);

	// Component State
	const [tooltipEnabled, setTooltipEnabled] = React.useState(null);
	const [tooltipAlwaysVisible, setTooltipAlwaysVisible] = React.useState(null);
	const [tooltipNote, setTooltipNote] = React.useState(null);
	const [tooltipNoteColor, setTooltipNoteColor] = React.useState(null);
	const [tooltipNoteIcon, setTooltipNoteIcon] = React.useState(null);

	let tooltipDataSourceName = 'ignore-conditional-note-datasource';
	if (tooltip) {
		tooltipDataSourceName = tooltip?.configuration?.dataSource?.name !== undefined ? tooltip.configuration.dataSource.name : tooltipDataSourceName;
	}
	const tooltipDataSource = useDataSource(tooltipDataSourceName);

	useEffect(() => {

		// console.log('-----------------Tooltip---------STATUS->');
		// console.log('fetching: {0}'.format(tooltipDataSource.fetching));
		// console.log('fetched: {0}'.format(tooltipDataSource.fetched));
		// console.log('error: {0}'.format(tooltipDataSource.error));
		// console.log('data:', tooltipDataSource.data);

		let tooltipSet = false;

		if (tooltipDataSource.data && tooltipDataSource.data.length > 0) {

			// NOTE: Another GID dependancy and view dependancy to return null strings
			const distinctTips = [...new Set(tooltipDataSource.data.map(d => d.gid))];
			// console.log('distinct ids ->', distinctTips.length);

			if (distinctTips.length === 1) {
				// console.log('tooltip ->', tooltipDataSource);

				// Pull the note record out of the dataset i.e. noteTileField === note.tile
				const note = tooltipDataSource.data.find(n => n[tooltip.configuration.dataSource.tileField].toString().toLowerCase() === tooltip.configuration.element.tile.toString().toLowerCase())
				// console.log('note ->', note);

				if (note) {

					// Ensure that at least one of the note fields has data
					// NOTE: We're not validating all fields/values as this
					// is config
					let buildNote = false;

					const noteFields = tooltip.configuration.dataSource.fields;
					for (let i = 0; i < noteFields.length; i++) {
						if (note[noteFields[i]]) {
							buildNote = true;
							break;
						}
					}

					// Pull out the note
					if (buildNote) {
						tooltipSet = true;

						// Grab the template otherwise we assume one field straight up
						let template = tooltip.configuration.dataSource.template ? tooltip.configuration.dataSource.template : "{0}";

						// Get and apply formatters from dataSource or note\tooltip config
						for (let i = 0; i < noteFields.length; i++) {

							const field = noteFields[i];
							const dataSetFormatters = getFieldFormatters(tooltipDataSource, field);
							const overrideFormatters = i < tooltip.configuration.dataSource.fieldFormatters.length ? tooltip.configuration.dataSource.fieldFormatters[i] : [];

							const formattedValue = formatValue(note[noteFields[i]], dataSetFormatters, overrideFormatters);

							template = template.replace("{" + i.toString() + "}", formattedValue);
						}

						setTooltipNote(template);
					} else {
						setTooltipNote(null);
					}

					// Pull out the color
					if (note[tooltip.configuration.dataSource.colorField]) {
						if (note[tooltip.configuration.dataSource.colorField].toLowerCase() === 'primary') {
							setTooltipNoteColor('primary');
						} else if (note[tooltip.configuration.dataSource.colorField].toLowerCase() === 'secondary') {
							setTooltipNoteColor('secondary');
						} else {
							setTooltipNoteColor(note[tooltip.configuration.dataSource.colorField].toLowerCase());
						}
					} else {
						setTooltipNoteColor('primary');
					}

					// Pull out the icon
					if (note[tooltip.configuration.dataSource.iconField]) {
						if (note[tooltip.configuration.dataSource.iconField].toLowerCase() === 'warning') {
							setTooltipNoteIcon('warning');
						} else if (note[tooltip.configuration.dataSource.iconField].toLowerCase() === 'accolade') {
							setTooltipNoteIcon('accolade');
						} else if (note[tooltip.configuration.dataSource.iconField].toLowerCase() === 'note') {
							setTooltipNoteIcon('note');
						} else {
							setTooltipNoteIcon('help');
						}
					} else {
						setTooltipNoteIcon('help');
					}
				}
			}
		}

		if (!tooltipSet) {
			// Clear any note references...notes are supported
			// at projects 1-1
			setTooltipNote(null);
		}
	}, [tooltipDataSource.data, tooltipDataSource.fetching, tooltipDataSource.fetched, tooltipDataSource.error]);

	useEffect(() => {
		if (tooltip && tooltip.configuration.element.enabled === true) {
			setTooltipEnabled(true);
		} else {
			setTooltipEnabled(false);
		}
		if (tooltip && tooltip.configuration.element.alwaysVisible === true) {
			setTooltipAlwaysVisible(true);
		} else {
			setTooltipAlwaysVisible(false);
		}
	}, [tooltip]);

	// Tooltip Popover
	const handlePopoverOpen = (event) => {
		displayTooltip(event);
	};
	const handlePopoverClose = () => {
		displayTooltip(null);
	};

	function displayTooltip(event) {
		if (tooltip.enabled === false) {
			return;
		}

		const message = tooltip.configuration.element.message;
		const lineBreak = tooltip.configuration.element.message && tooltip.configuration.element.message.length > 0 ? "<br/>" : "";
		const note = tooltipNote ? tooltipNote : "";

		const tooltipConfig = {
			enabled: tooltip.configuration.element.enabled,
			message: "{0}{1}{2}".format(message, lineBreak, note),
			note: {
				dataSource: tooltip.configuration.dataSource ? tooltip.configuration.dataSource : null,
				tile: tooltip.configuration.element?.tile !== undefined ? tooltip.configuration.element.tile : null,
				noteField: tooltip.configuration.dataSource?.noteField !== undefined ? tooltip.configuration.dataSource.noteField : null,
				noteTileField: tooltip.configuration.dataSource?.tileField !== undefined ? tooltip.configuration.dataSource.tileField : null,
				noteTemplate: tooltip.configuration.dataSource?.template !== undefined ? tooltip.configuration.dataSource.template : null,
				noteIconField: tooltipNoteIcon,
				noteColorField: tooltip.configuration.dataSource?.colorField !== undefined ? tooltip.configuration.dataSource.colorField : null
			},
			button: tooltip?.button !== undefined ? tooltip.button : {right: "8px", top: "8px"},
			paper: tooltip?.paper !== undefined ? tooltip.paper : {},
			open: event ? true : false,
			anchorEl: tooltipElement.current
		}

		dispatch(appSetTooltipContent(tooltipConfig));
	}

	let tooltipCustomColorStyle = {};
	if (tooltipNoteColor && tooltipNoteColor.indexOf('#') === 0) {
		tooltipCustomColorStyle.color = tooltipNoteColor;
	}

	if (tooltipEnabled === false) {
		return (
			<React.Fragment/>
		);
	}

	if (!tooltipAlwaysVisible && !focused && !tooltipNote)
	{
		return (
			<React.Fragment/>
		);
	}

	if ((!tooltip.configuration.element.message || tooltip.configuration.element.message.length === 0) && (!tooltipNote && tooltip?.configuration?.dataSource?.name)) {
		return (
			<React.Fragment/>
		);
	}

	return (
        <StyledTypography aria-owns={tooltip.open ? 'mouse-over-popover' : undefined}
					aria-haspopup="true"
					onMouseEnter={handlePopoverOpen}
					onMouseLeave={handlePopoverClose}
		>
			{!tooltipNote &&
				// Basic Grey Tooltip
				<LiveHelpIcon ref={tooltipElement} fontSize="small" style={{...tooltip.button}} className={classes.tooltipPopover} />
			}
			{(tooltipNote && tooltipNoteIcon) === "warning" &&
				// Notes with Colored Icons
				<WarningIcon ref={tooltipElement} fontSize="small" color={tooltipNoteColor} style={{...tooltip.button, ...tooltipCustomColorStyle}} className={classes.tooltipNotePopover} />
			}
			{(tooltipNote && tooltipNoteIcon) === "accolade" &&
				// Notes with Colored Icons
				<StarsIcon ref={tooltipElement} fontSize="small" color={tooltipNoteColor} style={{...tooltip.button, ...tooltipCustomColorStyle}} className={classes.tooltipNotePopover} />
			}
			{(tooltipNote && tooltipNoteIcon) === "note" &&
				// Notes with Colored Icons
				<NoteIcon ref={tooltipElement} fontSize="small" color={tooltipNoteColor} style={{...tooltip.button, ...tooltipCustomColorStyle}} className={classes.tooltipNotePopover} />
			}
			{(tooltipNote && tooltipNoteIcon) === "help" &&
				// Notes with Colored Icons
				<LiveHelpIcon ref={tooltipElement} fontSize="small" color={tooltipNoteColor} style={{...tooltip.button, ...tooltipCustomColorStyle}} className={classes.tooltipNotePopover} />
			}
		</StyledTypography>
    );
}
